import { useState } from 'react'
import '../Staffs/style.css'
import CustomTable from '../../components/Table'
import CustomTableHead from '../../components/Table/TableHead'
import CustomTableBody from '../../components/Table/TableBody'
import CustomTableRow from '../../components/Table/TableRow'
import CustomTableData from '../../components/Table/TableData'
import TablePagination from '../../components/Table/Pagination'
import ActivityService from '../../api/activity-service'
import CustomTableDataCTA from '../../components/Table/TableDataCTA'
import profileIcon from '../../assets/icons/profile-icon.svg'
import ActivityModal from '../../components/Activity/Modal/activity-modal'
import { formatedDate } from '../../utils/formatDate'
import SkeletonLoader from './SkeletonLoader'

const tableHeadData = ['Initiator', 'Action', 'Time', 'Position', 'Description', '']

interface ViewActivitiesProps {
  handleViewSelect: (view: string, queries?: Record<string, string>) => void
}

const PAGE_SIZE = 8

const ViewActivities = ({ handleViewSelect }: ViewActivitiesProps) => {
  const [openModal, setOpenModal] = useState<boolean>(false)
  const [selectedActivity, setSelectedActivity] = useState<any>(null)
  const [currentPage, setCurrentPage] = useState(1)

  const { data, isLoading } = ActivityService.useGetActivityList({
    page: currentPage,
    size: PAGE_SIZE,
  })

  const activitiesList = data?.data?.results || []
  const totalPages = Math.max(data?.data?.totalPages || 1, 1)

  const handleModalOpen = (activity: any) => {
    setSelectedActivity(activity)
    setOpenModal(true)
  }

  const handleModalClose = () => {
    setOpenModal(false)
    setSelectedActivity(null)
  }

  // Show skeleton loader while loading
  if (isLoading) {
    return (
      <div className="mt-4 sm:mt-6 md:mt-8 lg:mt-12 px-2 sm:px-3 md:px-0">
        <SkeletonLoader />
      </div>
    )
  }

  // Empty state when no activities
  if (!isLoading && activitiesList.length === 0) {
    return (
      <div className="mt-4 sm:mt-6 md:mt-8 lg:mt-12 px-2 sm:px-3 md:px-0">
        <div className="flex flex-col items-center justify-center py-12 sm:py-16 md:py-20 bg-gray-50 rounded-lg">
          <svg
            className="w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 text-gray-400 mb-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
          <p className="text-base sm:text-lg font-medium text-gray-600 text-center">
            No activities found
          </p>
          <p className="text-xs sm:text-sm text-gray-400 text-center mt-2">
            There are no activity records to display
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="mt-4 sm:mt-6 md:mt-8 lg:mt-12 grid gap-y-4 sm:gap-y-6 md:gap-y-8 lg:gap-y-10 px-2 sm:px-3 md:px-0">
      {/* Table Container with Horizontal Scroll for Mobile */}
      <div className="w-full overflow-x-auto -mx-2 sm:mx-0 px-2 sm:px-0">
        <CustomTable>
          <CustomTableHead dataList={tableHeadData} />
          <CustomTableBody>
            {activitiesList.map((activity) => (
              <CustomTableRow 
                key={activity.id} 
                className="hover:bg-gray-50 transition-colors"
              >
                {/* Initiator Column - Mobile Optimized */}
                <CustomTableData 
                  dataLabel="Initiator" 
                  text="" 
                  className="px-3 sm:px-4 md:px-6"
                >
                  <div className="flex items-center space-x-2 sm:space-x-3">
                    <img
                      src={activity.imageUrl || profileIcon}
                      alt="Profile"
                      className="w-8 h-8 sm:w-9 sm:h-9 md:w-10 md:h-10 rounded-full object-cover flex-shrink-0"
                    />
                    <div className="min-w-0 flex-1">
                      <p className="text-black-700 font-semibold text-xs sm:text-sm md:text-base truncate">
                        {activity.initiator?.fullName || 
                         activity.initiator?.name || 
                         'N/A'}
                      </p>
                      <p className="text-gray-400 text-[10px] sm:text-xs truncate">
                        {activity.initiator?.email || 'Not available'}
                      </p>
                    </div>
                  </div>
                </CustomTableData>

                {/* Action Column */}
                <CustomTableData 
                  dataLabel="Action" 
                  text={activity.action?.replace(/_/g, ' ') || 'N/A'}
                  className="px-3 sm:px-4 md:px-6 text-xs sm:text-sm md:text-base break-words"
                />

                {/* Time Column */}
                <CustomTableData 
                  dataLabel="Time" 
                  text={formatedDate(activity.createdAt) || 'N/A'}
                  className="px-3 sm:px-4 md:px-6 text-xs sm:text-sm md:text-base"
                />

                {/* Position Column */}
                <CustomTableData 
                  dataLabel="Position" 
                  text={activity.userType || 'N/A'}
                  className="px-3 sm:px-4 md:px-6 text-xs sm:text-sm md:text-base"
                />

                {/* Description Column - Hidden on smallest screens */}
                <CustomTableData 
                  dataLabel="Description" 
                  text={activity.description || 'N/A'}
                  className="hidden sm:table-cell px-3 sm:px-4 md:px-6 text-xs sm:text-sm md:text-base break-words max-w-[200px] lg:max-w-none"
                />

                {/* Actions Column */}
                <CustomTableDataCTA 
                  dataLabel="More" 
                  text="..."
                >
                  <p
                    onClick={() => handleModalOpen(activity)}
                    className="hover:bg-gray-200 p-2 sm:p-3 border-t cursor-pointer text-xs sm:text-sm transition-colors"
                  >
                    View details
                  </p>
                </CustomTableDataCTA>
              </CustomTableRow>
            ))}
          </CustomTableBody>
        </CustomTable>
      </div>

      {/* Pagination - Only show if there are activities and more than 1 page */}
      {activitiesList.length > 0 && totalPages > 1 && (
        <div className="mt-4 sm:mt-6 md:mt-8 flex justify-center">
          <TablePagination
            totalPages={totalPages}
            currPage={data?.data?.page || currentPage}
            setCurrentPage={setCurrentPage}
          />
        </div>
      )}

      {/* Activity Modal */}
      <ActivityModal
        open={openModal}
        onClose={handleModalClose}
        activity={selectedActivity}
      />
    </div>
  )
}

export default ViewActivities